// First browser-preferred language enabled for the org, else English.
export const resolveDefaultLanguage = (enabled: string[]): string => {
  if (typeof navigator === 'undefined') return 'en';
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  const enabledSet = new Set(enabled);
  const match = prefs
    .map((pref) => (pref || '').split('-')[0].toLowerCase()) // 'zh-CN' -> 'zh'
    .find((base) => enabledSet.has(base));
  return match ?? 'en';
};

export interface UnsupportedBrowserLanguage {
  browserLanguage: string; // e.g. 'es-MX'
  browserBaseLanguage: string; // e.g. 'es'
}

// Top browser preference when none are enabled (so we fall back to English);
// null when there's no preference or any is supported.
export const detectUnsupportedBrowserLanguage = (
  enabled: string[],
): UnsupportedBrowserLanguage | null => {
  if (typeof navigator === 'undefined') return null;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  const top = prefs.find(Boolean);
  if (!top) return null;
  const enabledSet = new Set(enabled);
  const supported = prefs
    .map((pref) => (pref || '').split('-')[0].toLowerCase())
    .some((base) => enabledSet.has(base));
  if (supported) return null;
  return { browserLanguage: top, browserBaseLanguage: top.split('-')[0].toLowerCase() };
};

// One-time flags (first-visit default applied, not-supported event tracked),
// persisted together under one localStorage key.
const FLAGS_KEY = 'finderLanguageFlags';

type LanguageFlag = 'languageDefaulted' | 'notSupportedTracked';

function readFlags(): Partial<Record<LanguageFlag, boolean>> {
  try {
    const raw = localStorage.getItem(FLAGS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function hasFlag(flag: LanguageFlag): boolean {
  return readFlags()[flag] === true;
}

function markFlag(flag: LanguageFlag): void {
  try {
    localStorage.setItem(FLAGS_KEY, JSON.stringify({ ...readFlags(), [flag]: true }));
  } catch {
    // storage disabled — best effort.
  }
}

export function hasLanguageDefaultBeenApplied(): boolean {
  return hasFlag('languageDefaulted');
}

export function markLanguageDefaultApplied(): void {
  markFlag('languageDefaulted');
}

export function hasBrowserLanguageNotSupportedBeenTracked(): boolean {
  return hasFlag('notSupportedTracked');
}

export function markBrowserLanguageNotSupportedTracked(): void {
  markFlag('notSupportedTracked');
}
